// ─── Créer une discussion ─────────────────────────────────────────
// Feuille à paliers (template Core) : jamais une <Modal> nue — un
// GestureDetector n'y reçoit pas les gestes.
//
// Icône d'action ＋ et non 💾 : l'élément n'existe pas encore (convention
// app-wide fixée le 08/08).
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Switch, StyleSheet } from 'react-native';
import Feuille from '../ui/Feuille';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from '../auth/AuthContext';
import { creerDiscussion } from '../services/discussions';

export default function FeuilleDiscussion({ cible, membres = [], onFerme }) {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const s = useMemo(() => styles(UI), [UI]);
  const [titre, setTitre] = useState('');
  const [visibleATous, setVisibleATous] = useState(true);
  const [choisis, setChoisis] = useState([]);
  const [envoi, setEnvoi] = useState(false);

  const basculer = (uid) =>
    setChoisis(l => (l.includes(uid) ? l.filter(x => x !== uid) : [...l, uid]));

  // Le titre est le seul champ requis. Une discussion restreinte sans personne
  // d'autre reste valide : le créateur peut ajouter des membres ensuite.
  const valide = titre.trim().length > 0;

  const creer = async () => {
    if (!valide || envoi) return;
    setEnvoi(true);
    try {
      await creerDiscussion({
        cle: cible, titre,
        visibleATous, participants: choisis,
        uid: user?.uid, nom: user?.prenom || user?.email || '',
      });
      onFerme?.();
    } catch { setEnvoi(false); }
  };

  return (
    <Feuille
      visible
      titre={t('messagerie.nouvelleDiscussion')}
      onFermer={onFerme}
      action={{ icone: '＋', disabled: !valide || envoi, onPress: creer }}
    >
      <ScrollView contentContainerStyle={s.contenu} keyboardShouldPersistTaps="handled">
        <Text style={s.label}>{t('messagerie.sujet')}</Text>
        <TextInput
          style={[s.champ, !valide && titre.length > 0 && s.champErreur]}
          value={titre}
          onChangeText={setTitre}
          placeholder={t('messagerie.sujetExemple')}
          placeholderTextColor={UI.faint}
        />

        <View style={s.rang}>
          <Text style={s.labelInline}>{t('messagerie.visibleParTous')}</Text>
          <Switch
            value={visibleATous}
            onValueChange={setVisibleATous}
            trackColor={{ false: UI.champBord, true: UI.accent }}
            thumbColor="#fff"
          />
        </View>

        {!visibleATous && (
          <>
            <Text style={s.label}>{t('messagerie.membresDeLaDiscussion')}</Text>
            <Text style={s.aide}>{t('messagerie.membresHint')}</Text>
            {membres.length === 0 && (
              <Text style={s.aide}>{t('messagerie.aucunAutreMembre')}</Text>
            )}
            {membres.map(m => (
              <TouchableOpacity
                key={m.uid}
                style={[s.membre, choisis.includes(m.uid) && s.membreOn]}
                onPress={() => basculer(m.uid)}
                activeOpacity={0.7}
              >
                <Text style={s.membreTxt}>{choisis.includes(m.uid) ? '☑' : '☐'}  {m.nom}</Text>
              </TouchableOpacity>
            ))}
          </>
        )}
      </ScrollView>
    </Feuille>
  );
}

const styles = (UI) => StyleSheet.create({
  contenu: { padding: 14, paddingBottom: 28 },
  label: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 12, marginBottom: 7 },
  labelInline: { fontSize: 13.5, color: UI.ink, flex: 1 },
  aide: { fontSize: 12, color: UI.faint, marginBottom: 8, lineHeight: 17 },
  champ: {
    backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12,
    paddingVertical: 10, fontSize: 15, color: UI.ink,
    borderWidth: 0.5, borderColor: UI.champBord,
  },
  champErreur: { borderColor: UI.danger, borderWidth: 1.5 },
  rang: { flexDirection: 'row', alignItems: 'center', marginTop: 18 },
  membre: {
    borderRadius: 10, borderWidth: 1, borderColor: UI.border,
    backgroundColor: UI.champ, padding: 11, marginBottom: 7,
  },
  membreOn: { borderColor: UI.accent, backgroundColor: UI.accentTint },
  membreTxt: { fontSize: 14, color: UI.ink },
});
